import type { HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useInternalStatus, type ValidationStatus } from '../hooks/useInternalStatus';
import { FormLabel, type FormLabelProps } from './FormLabel';
import clsx from 'clsx';
import { FormControlError } from './FormControlError';

export type FormControlProps<
  Value = unknown,
  Attributes extends HTMLAttributes<HTMLElement> = InputHTMLAttributes<HTMLInputElement>,
> = {
  value?: Value;
  label?: ReactNode;
  labelClassName?: string;
  validationStatus?: ValidationStatus;
  onChange?: (name: string, value: Value) => void;
  dataTestId?: string;
  dataName?: string;
  errorMessage?: string;
} & Omit<Attributes & Partial<FormLabelProps>, 'onChange' | 'value'>;

export const FormControl = <Value = string,>({
  id,
  label,
  description,
  disabled,
  required,
  className,
  validationStatus = 'default',
  children,
  errorMessage,
  dataTestId,
  dataName,
  labelClassName,
  hideRequiredStar,
  layout,
}: FormControlProps<Value>) => {
  const internalStatus = useInternalStatus({
    isDisabled: !!disabled,
    validationStatus,
  });

  const shouldDisplayErrorMessage = internalStatus === 'error' && errorMessage;

  return (
    <div
      className={clsx('flex flex-col gap-4', className)}
      data-name={dataName}
      data-testid={dataTestId}
    >
      {label && id && (
        <FormLabel
          description={description}
          id={id}
          layout={layout}
          required={required}
          hideRequiredStar={hideRequiredStar}
          className={labelClassName}
        >
          {label}
        </FormLabel>
      )}

      {children}

      {shouldDisplayErrorMessage && <FormControlError>{errorMessage}</FormControlError>}
    </div>
  );
};
